import { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Internal-only Unit Economics dashboard for DH Design.
 * Hidden URL: /admin/unit-economics
 *
 * Locked to a single master-admin email (not all admins).
 * Editable cost inputs so the breakdown stays accurate over time.
 * Built to be screenshot-friendly for partner conversations.
 */
const MASTER_ADMIN_EMAIL = 'hughes.d.ronald@gmail.com';
const GOLD = '#B89B5E';
const CHARCOAL = '#1C1C1C';
const IVORY = '#FAF8F5';
const SERIF = "'Playfair Display', serif";

const STRIPE_PCT = 0.029;
const STRIPE_FLAT = 0.30;

function fmtUSD(n) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
function fmtUSD2(n) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function netAfterStripe(gross) {
  return gross - (gross * STRIPE_PCT + STRIPE_FLAT);
}

export default function UnitEconomics() {
  const { user } = useAuth();

  // Editable costs (hooks must run before any conditional return)
  const [apollo, setApollo] = useState(79);
  const [serpapi, setSerpapi] = useState(32);
  const [emergent, setEmergent] = useState(50);
  const [llm, setLlm] = useState(20);
  const [domain, setDomain] = useState(2);
  const [other, setOther] = useState(0);

  const fixedOpEx = useMemo(
    () => apollo + serpapi + emergent + llm + domain + other,
    [apollo, serpapi, emergent, llm, domain, other]
  );

  // Master-admin gate — only the owner's email can view this page,
  // even other users with admin role are redirected to dashboard.
  if (user?.email?.toLowerCase() !== MASTER_ADMIN_EMAIL.toLowerCase()) {
    return <Navigate to="/dashboard" replace />;
  }

  const tiers = [
    { key: 'starter', name: 'Starter', price: 97 },
    { key: 'growth', name: 'Growth', price: 197 },
    { key: 'premium', name: 'Premium', price: 397 },
  ];

  const subCounts = [5, 10, 20, 50];

  const tiersWithNet = tiers.map((t) => ({
    ...t,
    stripeFee: t.price * STRIPE_PCT + STRIPE_FLAT,
    net: netAfterStripe(t.price),
  }));

  const breakEven = tiersWithNet.map((t) => ({
    name: t.name,
    subs: Math.ceil(fixedOpEx / t.net),
  }));

  return (
    <Layout>
      <div
        className="min-h-screen px-4 py-8 md:px-10 md:py-12 print:py-4 print:px-6"
        style={{ backgroundColor: IVORY }}
        data-testid="unit-economics-page"
      >
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-8 print:mb-4">
            <div>
              <p
                className="text-xs uppercase tracking-[0.25em] mb-2"
                style={{ color: GOLD }}
              >
                Internal — DH Design
              </p>
              <h1
                className="text-4xl md:text-5xl leading-tight"
                style={{ fontFamily: SERIF, color: CHARCOAL }}
              >
                Unit Economics
              </h1>
              <p className="text-sm mt-2" style={{ color: '#6B6B6B' }}>
                Monthly P&amp;L, break-even analysis, and rep commission model.
              </p>
            </div>
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="print:hidden"
              style={{ borderColor: CHARCOAL, color: CHARCOAL }}
              data-testid="unit-economics-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print / PDF
            </Button>
          </div>

          {/* Section 1: Cost inputs */}
          <Card className="mb-6 border-0 shadow-sm" style={{ backgroundColor: '#FFFFFF' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                01 — Monthly Fixed Operating Costs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 print:hidden">
                <CostInput label="Apollo.io API" value={apollo} onChange={setApollo} testid="cost-apollo" hint="$79 annual / $99 monthly" />
                <CostInput label="SerpAPI" value={serpapi} onChange={setSerpapi} testid="cost-serpapi" />
                <CostInput label="Emergent Platform" value={emergent} onChange={setEmergent} testid="cost-emergent" hint="Hosting + Mongo + domain" />
                <CostInput label="Emergent LLM Key (est.)" value={llm} onChange={setLlm} testid="cost-llm" hint="AI sweep + suggest patterns" />
                <CostInput label="Domain (amortized)" value={domain} onChange={setDomain} testid="cost-domain" />
                <CostInput label="Other" value={other} onChange={setOther} testid="cost-other" hint="Cloudflare $0, Maps $0 (free tier)" />
              </div>

              <table className="w-full text-sm" style={{ color: CHARCOAL }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${CHARCOAL}20` }}>
                    <th className="text-left py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Item</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Monthly</th>
                  </tr>
                </thead>
                <tbody>
                  <Row label="Apollo.io Professional (API)" value={apollo} />
                  <Row label="SerpAPI Developer" value={serpapi} />
                  <Row label="Emergent Platform" value={emergent} />
                  <Row label="Emergent LLM Key (consumption)" value={llm} />
                  <Row label="Domain (amortized)" value={domain} />
                  <Row label="Google Maps API" value={0} note="Free under $200/mo usage" />
                  <Row label="Cloudflare / DNS" value={0} note="Free tier" />
                  {other > 0 && <Row label="Other" value={other} />}
                  <tr style={{ borderTop: `2px solid ${CHARCOAL}` }}>
                    <td className="py-3 font-semibold uppercase tracking-wider text-xs">Total Fixed OpEx</td>
                    <td className="py-3 text-right font-semibold text-lg" style={{ fontFamily: SERIF, color: GOLD }} data-testid="total-fixed-opex">
                      {fmtUSD(fixedOpEx)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Section 2: Stripe impact */}
          <Card className="mb-6 border-0 shadow-sm" style={{ backgroundColor: '#FFFFFF' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                02 — Stripe Fee Impact (2.9% + $0.30 per transaction)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm" style={{ color: CHARCOAL }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${CHARCOAL}20` }}>
                    <th className="text-left py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Tier</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Gross</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Stripe Fee</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Net / Sub</th>
                  </tr>
                </thead>
                <tbody>
                  {tiersWithNet.map((t) => (
                    <tr key={t.key} style={{ borderBottom: `1px solid ${CHARCOAL}10` }}>
                      <td className="py-2">{t.name}</td>
                      <td className="py-2 text-right">{fmtUSD2(t.price)}</td>
                      <td className="py-2 text-right" style={{ color: '#B23A48' }}>−{fmtUSD2(t.stripeFee)}</td>
                      <td className="py-2 text-right font-semibold" style={{ color: GOLD }}>{fmtUSD2(t.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Section 3: P&L by tier */}
          <Card className="mb-6 border-0 shadow-sm" style={{ backgroundColor: '#FFFFFF' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                03 — Monthly P&amp;L at 5 / 10 / 20 / 50 Subscribers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tiersWithNet.map((t) => (
                <div key={t.key} className="mb-6 last:mb-0">
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="text-lg" style={{ fontFamily: SERIF, color: CHARCOAL }}>
                      {t.name} Tier — {fmtUSD(t.price)}/mo
                    </h3>
                    <span className="text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>
                      Net per sub: {fmtUSD2(t.net)}
                    </span>
                  </div>
                  <table className="w-full text-sm" style={{ color: CHARCOAL }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${CHARCOAL}20` }}>
                        <th className="text-left py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Subs</th>
                        <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Net Revenue</th>
                        <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>OpEx</th>
                        <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Net Profit</th>
                        <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subCounts.map((n) => {
                        const rev = t.net * n;
                        const profit = rev - fixedOpEx;
                        const margin = rev > 0 ? (profit / rev) * 100 : 0;
                        return (
                          <tr key={n} style={{ borderBottom: `1px solid ${CHARCOAL}08` }}>
                            <td className="py-2">{n}</td>
                            <td className="py-2 text-right">{fmtUSD(rev)}</td>
                            <td className="py-2 text-right" style={{ color: '#B23A48' }}>−{fmtUSD(fixedOpEx)}</td>
                            <td className="py-2 text-right font-semibold" style={{ color: profit >= 0 ? GOLD : '#B23A48' }}>
                              {fmtUSD(profit)}
                            </td>
                            <td className="py-2 text-right" style={{ color: '#6B6B6B' }}>{margin.toFixed(0)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Section 4: Break-even */}
          <Card className="mb-6 border-0 shadow-sm" style={{ backgroundColor: '#FFFFFF' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                04 — Break-Even
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {breakEven.map((b) => (
                  <div
                    key={b.name}
                    className="p-5 rounded-sm"
                    style={{ backgroundColor: IVORY, border: `1px solid ${GOLD}40` }}
                    data-testid={`break-even-${b.name.toLowerCase()}`}
                  >
                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#6B6B6B' }}>
                      All on {b.name}
                    </p>
                    <p className="text-4xl" style={{ fontFamily: SERIF, color: CHARCOAL }}>
                      {b.subs}
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#6B6B6B' }}>
                      {b.subs === 1 ? 'subscriber' : 'subscribers'} to cover {fmtUSD(fixedOpEx)} OpEx
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs mt-4" style={{ color: '#6B6B6B' }}>
                Realistic blended break-even at a 60% Starter / 30% Growth / 10% Premium mix:{' '}
                <strong style={{ color: CHARCOAL }}>
                  {Math.ceil(fixedOpEx / (0.6 * tiersWithNet[0].net + 0.3 * tiersWithNet[1].net + 0.1 * tiersWithNet[2].net))} paying subscribers
                </strong>.
              </p>
            </CardContent>
          </Card>

          {/* Section 5: Commission */}
          <Card className="mb-6 border-0 shadow-sm" style={{ backgroundColor: '#FFFFFF' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                05 — Rep Commission Model
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-5 p-4 rounded-sm" style={{ backgroundColor: CHARCOAL, color: '#FAF8F5' }}>
                <p className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>Recommendation</p>
                <p className="text-xl" style={{ fontFamily: SERIF }}>
                  20% recurring for 12 months, 5% lifetime thereafter
                </p>
                <p className="text-sm mt-2" style={{ color: '#D9D6D1' }}>
                  Reps running free trials act as both SDR and AE — recurring beats a one-time payout because it aligns them with customer retention, attracts career reps, and your margins (96–99% at scale) easily absorb it.
                </p>
              </div>

              <table className="w-full text-sm" style={{ color: CHARCOAL }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${CHARCOAL}20` }}>
                    <th className="text-left py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Tier</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Net / Sub</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Rep (mo 1–12)</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>You Keep (mo 1–12)</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Rep (mo 13+)</th>
                    <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>You Keep (mo 13+)</th>
                  </tr>
                </thead>
                <tbody>
                  {tiersWithNet.map((t) => {
                    const rep1 = t.net * 0.20;
                    const rep2 = t.net * 0.05;
                    return (
                      <tr key={t.key} style={{ borderBottom: `1px solid ${CHARCOAL}10` }}>
                        <td className="py-2">{t.name}</td>
                        <td className="py-2 text-right">{fmtUSD2(t.net)}</td>
                        <td className="py-2 text-right">{fmtUSD2(rep1)}</td>
                        <td className="py-2 text-right font-semibold" style={{ color: GOLD }}>{fmtUSD2(t.net - rep1)}</td>
                        <td className="py-2 text-right">{fmtUSD2(rep2)}</td>
                        <td className="py-2 text-right font-semibold" style={{ color: GOLD }}>{fmtUSD2(t.net - rep2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${CHARCOAL}15` }}>
                <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#6B6B6B' }}>
                  Alternative — One-Time Bonus (use if cash flow is tight)
                </p>
                <table className="w-full text-sm" style={{ color: CHARCOAL }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${CHARCOAL}20` }}>
                      <th className="text-left py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Tier</th>
                      <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Flat Payout</th>
                      <th className="text-right py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>≈ Months of MRR</th>
                      <th className="text-left py-2 font-normal text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: `1px solid ${CHARCOAL}10` }}>
                      <td className="py-2">Starter</td>
                      <td className="py-2 text-right">$200</td>
                      <td className="py-2 text-right">2.1×</td>
                      <td className="py-2 text-xs" style={{ color: '#6B6B6B' }}>Paid month 2 (post-retention)</td>
                    </tr>
                    <tr style={{ borderBottom: `1px solid ${CHARCOAL}10` }}>
                      <td className="py-2">Growth</td>
                      <td className="py-2 text-right">$400</td>
                      <td className="py-2 text-right">2.1×</td>
                      <td className="py-2 text-xs" style={{ color: '#6B6B6B' }}>Paid month 2 (post-retention)</td>
                    </tr>
                    <tr style={{ borderBottom: `1px solid ${CHARCOAL}10` }}>
                      <td className="py-2">Premium</td>
                      <td className="py-2 text-right">$800</td>
                      <td className="py-2 text-right">2.1×</td>
                      <td className="py-2 text-xs" style={{ color: '#6B6B6B' }}>Paid month 2 (post-retention)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Footer summary */}
          <Card className="border-0 shadow-sm" style={{ backgroundColor: CHARCOAL, color: '#FAF8F5' }}>
            <CardContent className="py-6">
              <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>
                Partner Conversation Summary
              </p>
              <p className="text-base leading-relaxed">
                Fixed monthly costs: <strong style={{ color: GOLD }}>{fmtUSD(fixedOpEx)}</strong>.
                Break-even at <strong style={{ color: GOLD }}>{Math.ceil(fixedOpEx / tiersWithNet[1].net)} Growth subscriber{Math.ceil(fixedOpEx / tiersWithNet[1].net) === 1 ? '' : 's'}</strong> or <strong style={{ color: GOLD }}>{Math.ceil(fixedOpEx / tiersWithNet[0].net)} Starter subscribers</strong>.
                Gross margin per net-of-Stripe dollar: <strong style={{ color: GOLD }}>96–99% at 20+ subs</strong>.
                Recommended rep commission: <strong style={{ color: GOLD }}>20% recurring for 12 months, 5% lifetime after</strong> — keeps margins above 75% in year one while aligning reps with retention.
              </p>
            </CardContent>
          </Card>

          <p className="text-xs text-center mt-6" style={{ color: '#6B6B6B' }}>
            Generated by DH Design internal tooling. Edit cost inputs above to update figures live.
          </p>
        </div>
      </div>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </Layout>
  );
}

function CostInput({ label, value, onChange, hint, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider" style={{ color: '#6B6B6B' }}>{label}</Label>
      <div className="relative mt-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#6B6B6B' }}>$</span>
        <Input
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="pl-7 h-9 text-sm"
          data-testid={testid}
        />
      </div>
      {hint && <p className="text-[10px] mt-1" style={{ color: '#999' }}>{hint}</p>}
    </div>
  );
}

function Row({ label, value, note }) {
  return (
    <tr style={{ borderBottom: `1px solid #00000008` }}>
      <td className="py-2">
        {label}
        {note && <span className="text-xs ml-2" style={{ color: '#999' }}>{note}</span>}
      </td>
      <td className="py-2 text-right">{fmtUSD(value)}</td>
    </tr>
  );
}
